import {
  AcoFormData,
  AdgroupFormData,
  AppInfo,
  CampaignFormData,
  FullNormalFormData,
  RejectInfo,
  SupportShowPixelObjectives,
  PixelType,
  PlatformType,
} from '@tt4b/ttam-enums';
import {
  useAllowlist,
  useStdCreativeAdgroupIs,
  useCreativeAdgroupIs,
  useStdObjectiveType,
  useAdgroupIos14,
  useTrackingFormEnableStatus,
  useStdInventoryFlow,
  useSdkS2s,
  useAuditInfo,
  useStdCampaignIs,
} from '@tt4b/ttam-hooks';

import { computed, Ref } from '@vue/composition-api';

export function useTrackUrl(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  appInfo: Ref<AppInfo>;
  createMode: Ref<string>;
  isDisabledCreative: Ref<boolean>;
}) {
  const { adgroupData, campaignData, creativeData, appInfo, createMode, isDisabledCreative } = opt;
  const { isDeeplinkWhite } = useAllowlist();

  const { isRetargetingApp } = useCreativeAdgroupIs({
    createMode,
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });
  const { isProductSalesCloseLoop } = useStdCampaignIs({ campaignFormData: campaignData });

  const {
    isShowWebsiteEvents: isShowWebsiteEventsModule,
    isShowAppEventsModule,
    isSupportAppEvents,
  } = useTrackingFormEnableStatus({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
  });

  const creativeConfig = computed(() => ({
    disableAll: isDisabledCreative.value,
  }));

  const { isDpa, isWebsite, isDpaLink, isAppDownload, isSupportTrackPixel } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isProductSales, isAppPreRegistration, isAppPromotion } = useStdObjectiveType({
    campaignFormData: campaignData,
  });

  const { hasTiktokAdNetwork } = useStdInventoryFlow({ adgroupFormData: adgroupData });

  const { isDedicateCampaign } = useAdgroupIos14({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  const isShowPixelOldStructure = computed(
    () =>
      (((SupportShowPixelObjectives.includes(adgroupData.value.objectiveType as number) && isWebsite.value) ||
        isProductSales.value) &&
        Boolean(adgroupData.value.adRefPixelId)) ||
      isSupportTrackPixel.value,
  );

  const isShowIOS14Tips = computed(
    () => (isAppPromotion.value || isProductSales.value) && hasTiktokAdNetwork.value && isDedicateCampaign.value,
  );

  const isShowWebsiteEvents = computed(() => !adgroupData.value?.adRefPixelId && isShowWebsiteEventsModule.value);
  const isShowAppEvents = computed(() => !adgroupData.value?.adRefAppId && isShowAppEventsModule.value);

  const isShowPixel = computed(
    () => (isShowPixelOldStructure.value || isShowWebsiteEventsModule.value) && !isProductSalesCloseLoop.value,
  );

  const isHasEventsTrackingModule = computed(() =>
    [isShowWebsiteEventsModule.value, isShowAppEventsModule.value].some(Boolean),
  );

  const currentIsDpa = computed(() => isDpa.value && isDpaLink.value && isDeeplinkWhite.value);

  const appHasUrl = computed(() => {
    const { trackUrl: appInfoTrackUrl } = appInfo.value || {};
    return Boolean(appInfoTrackUrl) && Boolean(appInfoTrackUrl?.clickUrl.length);
  });

  const trackAppHasUrl = computed(() => {
    const { TrackingUrl } = creativeData.value?.trackingAppInfo || {};

    return TrackingUrl?.ClickUrl && Boolean(TrackingUrl?.ClickUrl.length);
  });

  /** tracking 模块选了 app 且其有 clickUrl，则使用它的 url，且不可更改  */
  const isHasAppEventsTrackUrl = computed(
    () => isSupportAppEvents.value && Boolean(Number(creativeData.value?.trackingAppId)) && trackAppHasUrl.value,
  );

  /** 是否禁用 点击监测URL */
  const isDisabledClickTrackUrl = computed(
    () =>
      ((isAppDownload.value || currentIsDpa.value || isAppPreRegistration.value) && Boolean(appHasUrl.value)) ||
      isHasAppEventsTrackUrl.value,
  );

  return {
    isShowIOS14Tips,
    isShowPixel,
    isDisabledClickTrackUrl,
    isShowWebsiteEvents,
    isShowAppEvents,
    isShowAppEventsModule,
    isHasEventsTrackingModule,
    isRetargetingApp,
    creativeConfig,
  };
}

export const useTrackUrlShow = (opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData>;
  appInfo: Ref<AppInfo>;
  rejectInfo: Ref<RejectInfo>;
}) => {
  const { campaignData, adgroupData, creativeData, appInfo, rejectInfo } = opt;
  const { isRealLiveAds } = useAuditInfo({
    rejectInfo,
  });
  const { isSdkS2s, isSanApp } = useSdkS2s({
    appInfo,
    creativeFormData: creativeData,
  });
  const { isLiveAds } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  /**
   * 是否展示监测链接模块
   * 1. 自归因不展示（TikTok SDK/API / Classic SAN）
   * 2. 直播流不展示
   */
  const isShowTrackingUrl = computed(() => !(isSdkS2s.value || isSanApp.value || isRealLiveAds.value));
  const shouldClickTrackShow = computed(() => !(isSdkS2s.value || isLiveAds.value));

  return {
    isSdkS2s,
    isSanApp,
    shouldClickTrackShow,
    isShowTrackingUrl,
  };
};
